/** Emissions by month, stacked or grouped by series (scope), with an honest axis.
 *
 * **The axis starts at zero, always**, because a bar's length is its claim. A month with no figure
 * (`null`) is not a zero: it draws a dashed tick on the baseline and the table says "no figures".
 * Stacked bars stack the values they were handed; the scale's maximum is the tallest stack, used for
 * geometry only and never printed as a total.
 *
 * Each month is one focusable group (a roving tab stop: ArrowLeft/Right, Home, End) named with the
 * month and every series' figure, and shows a tooltip on focus as well as on hover. `ChartTable`
 * carries the same figures for a screen reader. Bars rise once on mount and are still under reduced
 * motion.
 *
 * **Dashboard band additions (ADR-0212, P7).** Hovering or focusing a month dims the others, so the
 * month under the cursor reads first. A month with `future: true` has not happened yet: it draws a
 * dashed outline column and its tooltip says "Not yet reached", which is different from a month with
 * no figures (a dashed baseline mark, "no figures") - a future month is not a gap in the data. A
 * month with a `receipt` opens it on click or Enter when the caller passes `onOpenReceipt`, and then
 * it is a **button** (a mark that does something is not an image).
 *
 * **One rule for "still arriving" (design uplift U5).** Incompleteness is judged per series, not per
 * month for all of them: `month.notes[seriesKey]` says what is missing from that one segment
 * (`1 of 214 bills missing`), and only that segment is drawn pale (a lighter fill of its own colour,
 * with no outline per bar) when `pale` is set. A segment with a note and no `pale` stays solid and
 * carries a small cap in the warning colour, so one missing bill among hundreds is told rather than
 * turning a whole stack into a ghost. `month.complete === false` (the month has not ended) makes
 * every segment of that month pale. The legend names what pale means, and only when it is drawn.
 */

import { AxisBottom, AxisLeft } from "@visx/axis";
import { Group } from "@visx/group";
import { scaleBand, scaleLinear } from "@visx/scale";
import { motion, useReducedMotion } from "motion/react";
import { useRef, useState } from "react";
import type { KeyboardEvent } from "react";

import { quantity } from "../../lib/format";
import chart from "./Chart.module.css";
import { ChartTable } from "./ChartTable";
import { ChartTooltip } from "./ChartTooltip";
import { useChartAsTable } from "./chartView";
import { anchorWithin } from "./tipAnchor";
import type { TipAnchor } from "./tipAnchor";
import { canOpen, receiptProps } from "./receipt";
import type { OpenReceipt } from "./receipt";
import styles from "./MonthlyBars.module.css";
import { CHART, TICK_LABEL, colourOf } from "./chartTokens";
import type { ChartColour } from "./chartTokens";
import { useChartWidth } from "./useChartWidth";

export interface BarSeries {
  key: string;
  label: string;
  colour: ChartColour;
}

/** What is missing from one series' segment of one month. */
export interface SegmentNote {
  /** In the census's words: `1 of 214 bills missing`. */
  text: string;
  /** Draw this segment pale (the share missing is large). Without it the segment is solid with a cap. */
  pale?: boolean;
}

export interface BarMonth {
  key: string;
  /** Axis label: `Jan`. */
  label: string;
  /** For names and tooltips: `January 2025`. */
  full: string;
  /** Series key to value; `null` or absent is "no figure". */
  values: Readonly<Record<string, number | null>>;
  /** A month that has not happened yet: drawn as a dashed outline, never as a gap or a zero. */
  future?: boolean;
  /** `false` when the whole month is still arriving (it has not ended; default `true`). Every segment
   * of an incomplete month is NEVER drawn as a real drop: it is pale, and the tooltip's first line
   * says it is still arriving. */
  complete?: boolean;
  /** What is outstanding for the whole month, as the caller words it: `month not over`. */
  missing?: string;
  /** What is missing from each series' own segment. Only a series with something missing has a key. */
  notes?: Readonly<Record<string, SegmentNote>>;
  /** The month's total as the server states it, for the tooltip. Shown when several series stack. */
  total?: number | null;
  /** What a reader reads for each series' value and for the total, **formatted by the caller from
   * the server's exact string** (rule 5, G-P7-01). `values` and `total` are `Number`s placed on a
   * scale; printing them would round a float again, so when a caller has the exact figure it hands
   * the printed text here and the chart uses it for the tooltip, the accessible name and the table. */
  printed?: Readonly<Record<string, string>>;
  totalPrinted?: string;
  /** One more tooltip line the caller words (for instance how much of the month rests on estimates). */
  detail?: string;
  /** Where this month's receipt is. A month opens it only with the chart's `onOpenReceipt`. */
  receipt?: string;
}

const M = { top: 8, right: 8, bottom: 26 };
/** Pixels per character of a 12px label, and the gap kept between two labels. */
const CHAR = 6.6;
const GAP = 8;

/** The decimals an axis step needs: whole numbers when the step is a whole number or more. */
function decimalsFor(step: number): number {
  if (!Number.isFinite(step) || step >= 1) return 0;
  return step >= 0.1 ? 1 : 2;
}

export function MonthlyBars({
  months,
  series,
  mode = "stacked",
  unit,
  format = (n) => String(n),
  caption,
  height = 220,
  testId = "monthly-bars",
  onOpenReceipt,
  legendNote,
  rowHeading = "Month",
}: {
  months: readonly BarMonth[];
  series: readonly BarSeries[];
  mode?: "stacked" | "grouped";
  /** `tCO2e`, for the axis title, tooltips and the table. */
  unit: string;
  /** How a number is printed (the screen's formatter). */
  format?: (value: number) => string;
  caption: string;
  height?: number;
  testId?: string;
  onOpenReceipt?: OpenReceipt;
  /** One more legend entry the caller words, for the whole window (how much rests on estimates). */
  legendNote?: string;
  /** The first column's heading in the table view: `Month` for a monthly series, `Week` for a weekly
   * one (G-P7-11 - the weekly arrivals table was headed "Month" over rows that are weeks). */
  rowHeading?: string;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const width = useChartWidth(wrap, 640);
  const reduce = useReducedMotion();
  const asTable = useChartAsTable();
  const refs = useRef(new Map<number, SVGGElement>());
  const [focus, setFocus] = useState(0);
  const [hot, setHot] = useState<number | null>(null);
  const [tip, setTip] = useState<{ anchor: TipAnchor; title: string; lines: string[] } | null>(null);

  const present = (month: BarMonth, key: string) => month.values[key] ?? null;
  /** The printed figure of one series' value: the caller's exact text, else the number formatted. */
  const printedValue = (month: BarMonth, key: string, value: number) => month.printed?.[key] ?? format(value);
  const printedTotal = (month: BarMonth, value: number) => month.totalPrinted ?? format(value);

  const tallest = Math.max(
    0,
    ...months.map((month) =>
      mode === "stacked"
        ? series.reduce((sum, s) => sum + (present(month, s.key) ?? 0), 0)
        : Math.max(0, ...series.map((s) => present(month, s.key) ?? 0)),
    ),
  );
  const innerH = Math.max(40, height - M.top - M.bottom);

  // The gutter is as wide as the widest tick label, so a label never touches the card's edge.
  const y = scaleLinear<number>({ domain: [0, tallest > 0 ? tallest : 1], range: [innerH, 0], nice: true });
  const ticks = y.ticks(4);
  const decimals = decimalsFor(ticks.length > 1 ? Math.abs((ticks[1] ?? 0) - (ticks[0] ?? 0)) : 1);
  const tickText = (value: number) => quantity(value.toFixed(decimals + 2), decimals);
  const left = Math.max(28, Math.ceil(Math.max(...ticks.map((t) => tickText(t).length)) * CHAR) + GAP + 6);
  const innerW = Math.max(40, width - left - M.right);
  const x = scaleBand<string>({ domain: months.map((m) => m.key), range: [0, innerW], padding: 0.25 });
  const inner = scaleBand<string>({ domain: series.map((s) => s.key), range: [0, x.bandwidth()], padding: 0.1 });

  // Fewer labels rather than colliding ones: every n-th, n the smallest stride that leaves a gap.
  const widest = Math.max(1, ...months.map((m) => m.label.length));
  const stride = Math.max(1, Math.ceil((widest * CHAR + GAP) / Math.max(1, x.step())));
  const shown = months.filter((_, at) => at % stride === 0).map((m) => m.key);

  // A month that opens something is a pointer target: at least 24px wide (WCAG 2.5.8) even when the
  // band is narrower, so its neighbours' hit areas may overlap by a pixel or two but none is smaller.
  // 25, not 24: the band scale's step is a float, and a rect laid out as 23.99998px measured as under the
  // 24px floor at 375px (the gate's target-size sweep). One pixel of margin keeps it over the line.
  const hitWidth = Math.max(x.step(), 25);
  const isIncomplete = (month: BarMonth) => month.complete === false && !month.future;
  const isPale = (month: BarMonth, key: string) =>
    !month.future && (month.complete === false || month.notes?.[key]?.pale === true);
  const openable = (month: BarMonth) => canOpen(month.receipt, onOpenReceipt);
  const lines = (month: BarMonth) => {
    if (month.future) return ["Not yet reached"];
    const out = series.map((s) => {
      const value = present(month, s.key);
      const note = month.notes?.[s.key]?.text;
      return `${s.label}: ${value === null ? "no figures" : `${printedValue(month, s.key, value)} ${unit}`}${note ? ` · ${note}` : ""}`;
    });
    if (series.length > 1 && month.total !== undefined && month.total !== null) {
      out.push(`Total: ${printedTotal(month, month.total)} ${unit}`);
    }
    if (month.detail) out.push(month.detail);
    return out;
  };
  /** The tooltip's first line: the month, and for an incomplete one that it is, and what is missing. */
  const heading = (month: BarMonth) =>
    isIncomplete(month) ? `${month.full} · still arriving${month.missing ? `, ${month.missing}` : ""}` : month.full;
  const name = (month: BarMonth) =>
    `${heading(month)}. ${lines(month).join(". ")}${openable(month) ? ". Opens the figures" : ""}`;
  const hide = () => {
    setTip(null);
    setHot(null);
  };

  const show = (index: number) => {
    const month = months[index];
    const element = refs.current.get(index);
    if (!month || !element || !wrap.current) return;
    setHot(index);
    setTip({
      anchor: anchorWithin(wrap.current, element),
      title: heading(month),
      lines: [...lines(month), ...(openable(month) ? ["Click to open the figures"] : [])],
    });
  };
  const move = (index: number) => {
    const next = Math.max(0, Math.min(months.length - 1, index));
    setFocus(next);
    refs.current.get(next)?.focus();
  };
  const onKey = (event: KeyboardEvent<SVGGElement>, index: number) => {
    if (event.key === "ArrowRight") move(index + 1);
    else if (event.key === "ArrowLeft") move(index - 1);
    else if (event.key === "Home") move(0);
    else if (event.key === "End") move(months.length - 1);
    else if (event.key === "Escape") hide();
    else return;
    event.preventDefault();
  };

  const transition = reduce ? { duration: 0 } : ({ duration: 0.6, ease: [0.16, 1, 0.3, 1] } as const);
  const bar = (
    key: string,
    colour: string,
    bx: number,
    by: number,
    bw: number,
    bh: number,
    pale: boolean,
    capped: boolean,
  ) => (
    <g key={key}>
      <motion.rect
        x={bx}
        y={by}
        width={bw}
        height={Math.max(0, bh)}
        rx={2}
        style={{
          fill: colour,
          transformBox: "fill-box",
          transformOrigin: "center bottom",
          ...(pale ? { fillOpacity: 0.32 } : {}),
          ...(mode === "stacked" ? { stroke: CHART.surface, strokeWidth: 2 } : {}),
        }}
        data-incomplete={pale ? "true" : undefined}
        initial={reduce ? false : { scaleY: 0 }}
        animate={{ scaleY: 1 }}
        transition={transition}
      />
      {capped && bh > 0 ? (
        <rect
          x={bx}
          y={by}
          width={bw}
          height={Math.min(5, bh)}
          rx={1}
          fill={CHART.attention}
          stroke={CHART.surface}
          strokeWidth={1}
          data-cap="true"
          aria-hidden="true"
        />
      ) : null}
    </g>
  );

  const anyPale = months.some((m) => series.some((s) => present(m, s.key) !== null && isPale(m, s.key)));
  const anyCap = months.some((m) => !m.future && series.some((s) => m.notes?.[s.key] !== undefined));
  const withTotal = months.some((m) => m.total !== undefined && m.total !== null) && series.length > 1;

  const table = (
    <ChartTable
      visible={asTable}
      caption={caption}
      head={[rowHeading, ...series.map((s) => `${s.label} (${unit})`), ...(withTotal ? [`Total (${unit})`] : [])]}
      rows={months.map((month) => [
        isIncomplete(month)
          ? `${month.full} (still arriving${month.missing ? `, ${month.missing}` : ""})`
          : month.full,
        ...series.map((s) => {
          const value = present(month, s.key);
          if (month.future) return "not yet reached";
          if (value === null) return "no figures";
          const note = month.notes?.[s.key]?.text;
          const text = printedValue(month, s.key, value);
          return note ? `${text} (${note})` : text;
        }),
        ...(withTotal
          ? [month.future ? "not yet reached" : month.total === undefined || month.total === null ? "no figures" : printedTotal(month, month.total)]
          : []),
      ])}
      note={`Every bar starts at zero. A month with no figures is shown as a dashed mark, not as zero. A pale bar is still arriving: its figure is what has arrived so far.${legendNote ? ` ${legendNote}.` : ""}`}
    />
  );
  if (asTable) {
    return (
      <div ref={wrap} className={chart.chart} data-testid={testId}>
        {table}
      </div>
    );
  }

  return (
    <div ref={wrap} className={chart.chart} data-testid={testId}>
      <ul className={chart.legend} data-testid="monthly-legend">
        {series.map((s) => (
          <li key={s.key} className={chart.legendItem}>
            <span className={chart.swatch} aria-hidden="true" style={{ background: colourOf(s.colour) }} />
            {s.label}
          </li>
        ))}
        {anyPale ? (
          <li className={chart.legendItem} data-testid="legend-arriving">
            <span className={`${chart.swatch} ${styles.swatchPale}`} aria-hidden="true" />
            Still arriving
          </li>
        ) : null}
        {anyCap ? (
          <li className={chart.legendItem} data-testid="legend-missing">
            <span className={`${chart.swatch} ${styles.swatchCap}`} aria-hidden="true" />
            Bills missing
          </li>
        ) : null}
        {legendNote ? (
          <li className={chart.legendItem} data-testid="legend-note">
            {legendNote}
          </li>
        ) : null}
      </ul>
      <svg
        width={width}
        height={height}
        role={months.some(openable) ? "toolbar" : "group"}
        aria-orientation={months.some(openable) ? "horizontal" : undefined}
        aria-label={caption}
      >
        <Group left={left} top={M.top}>
          <g aria-hidden="true">
          <AxisLeft
            scale={y}
            tickValues={ticks}
            tickFormat={(v) => tickText(Number(v))}
            stroke={CHART.axis}
            tickStroke={CHART.axis}
            tickLabelProps={{ ...TICK_LABEL, textAnchor: "end", dy: "0.33em", dx: "-0.4em" }}
          />
          {ticks.map((t) => (
            <line key={t} x1={0} x2={innerW} y1={y(t)} y2={y(t)} stroke={CHART.grid} />
          ))}
          <AxisBottom
            top={innerH}
            scale={x}
            stroke={CHART.axis}
            tickStroke="transparent"
            tickValues={shown}
            tickFormat={(key) => months.find((m) => m.key === key)?.label ?? key}
            tickLabelProps={{ ...TICK_LABEL, textAnchor: "middle" }}
          />
          </g>
          {months.map((month, index) => {
            const at = x(month.key) ?? 0;
            const none = series.every((s) => present(month, s.key) === null);
            const open = receiptProps(month.receipt, month.key, onOpenReceipt);
            let stacked = 0;
            return (
              <g
                key={month.key}
                ref={(node) => {
                  if (node) refs.current.set(index, node);
                  else refs.current.delete(index);
                }}
                tabIndex={focus === index ? 0 : -1}
                role={open.onClick ? "button" : "img"}
                aria-label={name(month)}
                className={`${styles.month} ${hot !== null && hot !== index ? styles.dim : ""} ${open.onClick ? styles.openable : ""}`}
                data-testid="month-bar"
                data-future={month.future ? "true" : undefined}
                data-receipt={open["data-receipt"]}
                onClick={open.onClick}
                onKeyDown={(event) => {
                  onKey(event, index);
                  if (!event.defaultPrevented) open.onKeyDown?.(event);
                }}
                onFocus={() => {
                  setFocus(index);
                  show(index);
                }}
                onBlur={hide}
                onPointerEnter={() => show(index)}
                onPointerLeave={hide}
              >
                <rect x={at + x.bandwidth() / 2 - hitWidth / 2} y={0} width={hitWidth} height={innerH} className={styles.hit} />
                {month.future ? (
                  <rect
                    x={at}
                    y={0}
                    width={x.bandwidth()}
                    height={innerH}
                    rx={2}
                    className={styles.future}
                    data-testid="future-month"
                  />
                ) : none ? (
                  <line x1={at} x2={at + x.bandwidth()} y1={innerH - 1} y2={innerH - 1} className={styles.gap} />
                ) : (
                  series.map((s) => {
                    const value = present(month, s.key);
                    if (value === null) return null;
                    const colour = colourOf(s.colour);
                    const pale = isPale(month, s.key);
                    const capped = month.notes?.[s.key] !== undefined;
                    if (mode === "grouped") {
                      const top = y(value);
                      return bar(s.key, colour, at + (inner(s.key) ?? 0), top, inner.bandwidth(), innerH - top, pale, capped);
                    }
                    const top = y(stacked + value);
                    const h = y(stacked) - top;
                    stacked += value;
                    return bar(s.key, colour, at, top, x.bandwidth(), h, pale, capped);
                  })
                )}
              </g>
            );
          })}
        </Group>
      </svg>
      <ChartTooltip anchor={tip?.anchor ? { x: tip.anchor.x, y: tip.anchor.y } : null}>
        <b>{tip?.title}</b>
        {tip?.lines.map((line, at) => (
          <span key={`${String(at)}-${line}`} className={styles.tipLine}>
            {line}
          </span>
        ))}
      </ChartTooltip>
      {table}
    </div>
  );
}
